import { useState, useEffect } from 'react';
import { X, Cookie, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after 1 second
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const acceptAll = () => {
    const fullConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(fullConsent));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    const minimalConsent = {
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(minimalConsent));
    setShowBanner(false);
  };

  const savePreferences = () => {
    const customConsent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(customConsent));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="glass-card p-6 md:p-8 shadow-2xl border-t-4" style={{ borderTopColor: 'var(--gold-600)' }}>
          {!showSettings ? (
            // Main Banner
            <div>
              <div className="flex items-start gap-4 mb-6">
                <Cookie className="h-8 w-8 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--navy-800)' }}>
                    Wir respektieren Ihre Privatsphäre
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--gray-700)' }}>
                    Diese Website verwendet Cookies, um Ihnen die bestmögliche Nutzererfahrung zu bieten. 
                    Notwendige Cookies sind für den Betrieb der Website erforderlich. Weitere Cookies werden 
                    nur mit Ihrer Zustimmung verwendet.
                  </p>
                  <Link to="/cookies" className="text-sm hover:underline mt-2 inline-block" style={{ color: 'var(--navy-600)' }}>
                    Mehr über Cookies →
                  </Link>
                </div>
                <button
                  onClick={() => setShowBanner(false)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Schließen"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={acceptAll}
                  className="btn-gold-gradient text-white border-0 font-semibold"
                  data-testid="cookie-accept-all"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Alle akzeptieren
                </Button>
                <Button
                  onClick={acceptNecessary}
                  variant="outline"
                  className="font-semibold"
                  style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}
                  data-testid="cookie-accept-necessary"
                >
                  Nur notwendige
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="ghost"
                  className="font-semibold"
                  style={{ color: 'var(--navy-600)' }}
                  data-testid="cookie-settings-button"
                >
                  Einstellungen
                </Button>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div>
              <div className="flex items-start justify-between mb-6">
                <h3 className="text-xl font-bold" style={{ color: 'var(--navy-800)' }}>
                  Cookie-Einstellungen
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="h-4 w-4" style={{ color: 'var(--success-600)' }} />
                      <h4 className="font-semibold" style={{ color: 'var(--navy-700)' }}>Notwendige Cookies</h4>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-600)' }}>
                      Diese Cookies sind für den Betrieb der Website erforderlich und können nicht deaktiviert werden.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-semibold" style={{ color: 'var(--success-600)' }}>Immer aktiv</span>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1 pr-4">
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--navy-700)' }}>Funktionale Cookies</h4>
                    <p className="text-sm" style={{ color: 'var(--gray-600)' }}>
                      Ermöglichen erweiterte Funktionen und Personalisierung.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={preferences.functional}
                      onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[#D4A853] peer-focus:ring-2 peer-focus:ring-[#D4A853] transition-colors">
                      <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5"></div>
                    </div>
                  </label>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1 pr-4">
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--navy-700)' }}>Analyse-Cookies</h4>
                    <p className="text-sm" style={{ color: 'var(--gray-600)' }}>
                      Helfen uns, die Nutzung der Website zu analysieren.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[#D4A853] peer-focus:ring-2 peer-focus:ring-[#D4A853] transition-colors">
                      <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5"></div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={savePreferences}
                  className="btn-gold-gradient text-white border-0 font-semibold flex-1"
                >
                  Auswahl speichern
                </Button>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="outline"
                  style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;